"use client";

import { Sparkles, Star } from "lucide-react";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";

function ShopBreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-4 w-16 rounded-md bg-muted animate-pulse" />
        <span className="text-foreground/40">/</span>
        <div className="h-4 w-24 rounded-md bg-muted animate-pulse" />
      </div>
    );
  }

  if (error || links.length === 0) return null;

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList className="text-foreground/80 text-sm sm:text-base">
        {links.map((link, i) => (
          <BreadcrumbItem key={link.href} data-index={i}>
            {i === lastIndex ? (
              <BreadcrumbPage className="font-semibold text-foreground">
                {link.title}
              </BreadcrumbPage>
            ) : (
              <>
                <BreadcrumbLink
                  href={link.href}
                  className="hover:text-primary transition-colors font-medium"
                >
                  {link.title}
                </BreadcrumbLink>
                <BreadcrumbSeparator className="ml-1.5 text-foreground/50" />
              </>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTitleHeading() {
  const { links, loading } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div className="h-14 w-72 rounded-lg bg-muted animate-pulse" />
    );
  }

  const title =
    links.length > 0 ? links[links.length - 1].title : "Shop";

  return (
    <h1
      className="font-default text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-foreground leading-[0.95] tracking-tight"
      style={{ fontFamily: "var(--font-default)" }}
    >
      {title}
    </h1>
  );
}

export default function Archive() {
  return (
    <section data-section-id="1544"
      id="archive-title-section"
      className="relative bg-background text-foreground overflow-hidden border-b-2 border-border"
    >
      {}
      <div
        className="absolute top-6 right-6 sm:top-10 sm:right-12 text-primary opacity-90 rotate-12 hidden sm:block"
        aria-hidden="true"
      >
        <Star className="h-12 w-12 sm:h-16 sm:w-16 fill-primary" strokeWidth={2} />
      </div>
      <div
        className="absolute bottom-8 left-6 sm:left-12 text-accent-foreground opacity-90 -rotate-12 hidden sm:block"
        aria-hidden="true"
      >
        <Sparkles className="h-10 w-10 sm:h-14 sm:w-14" strokeWidth={2.5} />
      </div>
      <div
        className="absolute top-1/2 right-1/4 h-6 w-6 sm:h-8 sm:w-8 rounded-lg bg-primary border-2 border-border rotate-45 hidden md:block"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-1/3 right-10 h-4 w-4 sm:h-5 sm:w-5 rounded-lg bg-primary/20 border-2 border-border hidden md:block"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-24">
        <ShopBreadcrumbsProvider className="flex flex-col gap-6 sm:gap-8">
          {}
          <div className="inline-flex w-fit items-center px-4 py-2 rounded-lg bg-card border-2 border-border shadow-md">
            <ShopBreadcrumbsConsumer />
          </div>

          {}
          <div className="flex flex-col gap-4 max-w-4xl">
            <div className="inline-flex w-fit items-center gap-2 px-3 py-1 rounded-md bg-primary border-2 border-border">
              <Sparkles className="h-4 w-4 text-primary-foreground" strokeWidth={2.5} />
              <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-primary-foreground font-default">
                DTF Catalog
              </span>
            </div>

            <ArchiveTitleHeading />

            {}
            <svg
              className="text-primary -mt-2 ml-1"
              width="180"
              height="14"
              viewBox="0 0 180 14"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M2 8C30 3 60 11 90 6C120 1 150 10 178 5"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </ShopBreadcrumbsProvider>
      </div>
    </section>
  );
}